import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Capability, UserRole } from '@admitly/shared';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../lib/api.js';

export type StaffUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  active: boolean;
  office: { code: string; name: string } | null;
  agency: { id: string; name: string } | null;
};

export type UserCapabilities = {
  userId: string;
  role: UserRole;
  effective: Capability[];
  overrides: { capability: Capability; granted: boolean }[];
};

export type BranchOption = {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
};

const usersKey = ['admin', 'access', 'users'] as const;
const capsKey = (userId: string) => [...usersKey, userId, 'capabilities'] as const;
const branchesKey = (userId: string) => [...usersKey, userId, 'branches'] as const;

/** The master-admin staff list (Brief 3b endpoint). */
export function useStaffList() {
  return useQuery({
    queryKey: usersKey,
    queryFn: () => apiGet<{ users: StaffUser[] }>('/admin/access/users'),
    staleTime: 30_000,
  });
}

/** One user's effective capabilities + raw overrides. Disabled until a user is selected. */
export function useUserCapabilities(userId: string | null) {
  return useQuery({
    queryKey: userId ? capsKey(userId) : [...usersKey, '__none__', 'capabilities'],
    queryFn: () => apiGet<UserCapabilities>(`/admin/access/users/${userId}/capabilities`),
    enabled: !!userId,
  });
}

export function useSetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      apiPatch<{ ok: true; userId: string; role: UserRole }>(`/admin/access/users/${userId}/role`, {
        role,
      }),
    // A role change alters both the list row and the user's caps — invalidate the
    // whole prefix so the list AND any open capabilities query re-fetch.
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

/** The offices available for a user's tenant (the office-picker source). */
export function useBranchOptions(userId: string | null) {
  return useQuery({
    queryKey: userId ? branchesKey(userId) : [...usersKey, '__none__', 'branches'],
    queryFn: () => apiGet<{ branches: BranchOption[] }>(`/admin/access/users/${userId}/branches`),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useSetBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, branchId }: { userId: string; branchId: string | null }) =>
      apiPatch<{ ok: true; userId: string; branchId: string | null }>(
        `/admin/access/users/${userId}/branch`,
        { branchId },
      ),
    // A branch change alters the list row's office — invalidate the whole prefix.
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useSetCapabilityOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      capability,
      granted,
    }: {
      userId: string;
      capability: Capability;
      granted: boolean;
    }) => apiPut<{ ok: true }>(`/admin/access/users/${userId}/capabilities/${capability}`, { granted }),
    onSuccess: (_data, { userId }) => qc.invalidateQueries({ queryKey: capsKey(userId) }),
  });
}

export function useClearCapabilityOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, capability }: { userId: string; capability: Capability }) =>
      apiDelete<{ ok: true }>(`/admin/access/users/${userId}/capabilities/${capability}`),
    onSuccess: (_data, { userId }) => qc.invalidateQueries({ queryKey: capsKey(userId) }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ id: string; deactivated_at: string }>(`/admin/access/users/${userId}/deactivate`),
    // Invalidate the whole prefix so the list-row badge AND the open panel both refresh.
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ id: string; deactivated_at: null }>(`/admin/access/users/${userId}/reactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}
